import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Loader2, Search, Flame, TrendingUp, LogOut, RefreshCw, Filter,
  Building2, Calendar, Briefcase, ChevronRight, Inbox as InboxIcon
} from "lucide-react";
import { toast } from "sonner";
import { eosApi, getAdminKey, clearAdminKey } from "../../lib/eosApi";
import { useDocMeta } from "../../lib/useDocMeta";

// Story #6 — Founder Inbox. Sorted server-side by urgency_score, displayed
// with columns: Customer · Agency · Opportunity · Due Date · Priority ·
// Requested Service · Status · Assigned To · Received. Filter + search +
// quick-open engagement.

const PRIORITIES = ["", "URGENT", "HIGH", "MEDIUM", "LOW"];
const STATUSES = ["", "NEW", "IN_REVIEW", "ACCEPTED", "REJECTED"];
const SERVICES = ["", "proposal_writing", "capture_management", "compliance_review", "price_to_win", "teaming", "other"];

function priorityChip(p) {
  const s = {
    URGENT: { text: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/60", icon: Flame },
    HIGH:   { text: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/40", icon: TrendingUp },
    MEDIUM: { text: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/40", icon: TrendingUp },
    LOW:    { text: "text-slate-300", bg: "bg-slate-500/10", border: "border-slate-500/40", icon: TrendingUp },
  }[p || "MEDIUM"] || { text: "text-slate-300", bg: "bg-slate-500/10", border: "border-slate-500/40", icon: TrendingUp };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border ${s.border} ${s.bg}`}>
      <Icon className={`w-2.5 h-2.5 ${s.text}`} strokeWidth={2.2} />
      <span className={`font-mono text-[9px] tracking-[0.15em] uppercase ${s.text}`}>{p || "MEDIUM"}</span>
    </span>
  );
}

function statusChip(st) {
  const s = ({
    NEW:       { text: "text-sky-300",     border: "border-sky-500/50",    bg: "bg-sky-500/10" },
    IN_REVIEW: { text: "text-amber-300",   border: "border-amber-500/50",  bg: "bg-amber-500/10" },
    ACCEPTED:  { text: "text-emerald-300", border: "border-emerald-500/50", bg: "bg-emerald-500/10" },
    REJECTED:  { text: "text-rose-300",    border: "border-rose-500/50",   bg: "bg-rose-500/10" },
  })[st || "NEW"] || { text: "text-slate-300", border: "border-slate-500/40", bg: "bg-slate-500/10" };
  return <span className={`font-mono text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 border ${s.border} ${s.bg} ${s.text}`}>{st || "NEW"}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return "—"; }
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const s = (x) => { const c = new Date(x); c.setHours(0,0,0,0); return c; };
  return Math.round((s(d).getTime() - s(new Date()).getTime()) / 86400000);
}

function dueChip(iso) {
  const n = daysUntil(iso);
  if (n === null) return <span className="text-slate-500 text-xs">—</span>;
  const tone = n < 0 ? "rose" : n <= 3 ? "rose" : n <= 14 ? "amber" : "slate";
  const cls = {
    rose:  "text-rose-300 border-rose-500/40 bg-rose-500/10",
    amber: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    slate: "text-slate-300 border-slate-500/40 bg-slate-500/10",
  }[tone];
  const label = n < 0 ? "Overdue" : n === 0 ? "Due Today" : `${n}d`;
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className="font-mono text-[10px] text-slate-300">{fmtDate(iso)}</span>
      <span className={`font-mono text-[9px] tracking-[0.1em] uppercase px-1.5 py-0.5 border ${cls}`}>{label}</span>
    </div>
  );
}

export default function EosInbox() {
  useDocMeta({ title: "EOS Inbox · Founder Workspace", path: "/eos/inbox" });
  const nav = useNavigate();
  const hasKey = getAdminKey();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("");
  const [service, setService] = useState("");
  const [unassigned, setUnassigned] = useState(false);

  const load = async () => {
    if (!hasKey) return;
    setBusy(true);
    const params = { q, priority, status, requested_service: service, unassigned };
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    try {
      const res = await eosApi().get("/service_requests", { params });
      setItems(res.data);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Session expired"); nav("/eos"); }
      else toast.error("Failed to load inbox");
      setItems([]);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [q, priority, status, service, unassigned, hasKey]);

  const stats = useMemo(() => {
    const l = items || [];
    return {
      total: l.length,
      urgent: l.filter((x) => x.priority === "URGENT" || x.priority === "HIGH").length,
      unassigned: l.filter((x) => !x.assigned_to).length,
      overdue: l.filter((x) => { const n = daysUntil(x.desired_completion_date); return n !== null && n < 0; }).length,
    };
  }, [items]);

  if (!hasKey) return <Navigate to="/eos" replace />;

  const openEngagement = async (sr) => {
    // If engagement already exists, jump straight to it; otherwise create then jump.
    if (sr.engagement_id) { nav(`/eos/engagements/${sr.engagement_id}`); return; }
    try {
      const res = await eosApi().post("/engagements", { service_request_id: sr.id });
      nav(`/eos/engagements/${res.data.id}`);
    } catch (e) {
      toast.error("Failed to open engagement");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8" data-testid="eos-inbox">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="tick-label text-sky-400 mb-1">/ EOS · Founder Inbox</div>
          <h1 className="font-display font-black text-3xl text-slate-100">Proposal Services Inbox</h1>
          <p className="text-slate-400 text-sm mt-1">Sorted by urgency. Priority × due-date proximity × unassigned bump.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-2 border border-[var(--border-soft)] hover:border-sky-500/60 text-slate-300 font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2"
            data-testid="eos-inbox-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <button
            onClick={() => { clearAdminKey(); nav("/eos"); }}
            className="inline-flex items-center gap-2 border border-rose-500/40 hover:bg-rose-500/10 text-rose-300 font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2"
            data-testid="eos-inbox-logout"
          >
            <LogOut className="w-3 h-3" /> Lock
          </button>
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-4 gap-px bg-[var(--border-soft)] mb-6" data-testid="eos-inbox-stats">
        <Stat label="Total"       value={stats.total}      accent="sky" />
        <Stat label="Urgent+High" value={stats.urgent}     accent="rose" />
        <Stat label="Unassigned"  value={stats.unassigned} accent="amber" />
        <Stat label="Overdue"     value={stats.overdue}    accent="rose" />
      </div>

      {/* Filter bar */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-soft)] p-3 mb-4 flex flex-wrap gap-3 items-center" data-testid="eos-inbox-filters">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer, opportunity, agency, sol#…"
            data-testid="eos-inbox-search"
            className="flex-1 bg-transparent outline-none text-slate-100 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FilterSelect label="Priority" value={priority} onChange={setPriority} options={PRIORITIES} testid="eos-inbox-filter-priority" />
          <FilterSelect label="Status"   value={status}   onChange={setStatus}   options={STATUSES}   testid="eos-inbox-filter-status" />
          <FilterSelect label="Service"  value={service}  onChange={setService}  options={SERVICES}   testid="eos-inbox-filter-service" />
          <label className="inline-flex items-center gap-2 text-slate-300 text-xs cursor-pointer" data-testid="eos-inbox-filter-unassigned">
            <input type="checkbox" checked={unassigned} onChange={(e) => setUnassigned(e.target.checked)} className="accent-sky-500" />
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase">Unassigned only</span>
          </label>
        </div>
      </div>

      {/* Table */}
      {items === null ? (
        <div className="text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading inbox…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[var(--border-soft)] bg-[var(--bg-secondary)] p-10 text-center" data-testid="eos-inbox-empty">
          <InboxIcon className="w-8 h-8 text-slate-600 mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-display font-bold text-slate-100 mb-1">Inbox is clear</div>
          <div className="text-slate-500 text-sm">No service requests match the current filters.</div>
        </div>
      ) : (
        <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] overflow-x-auto" data-testid="eos-inbox-table">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-primary)]">
              <tr className="text-left">
                <Th>Customer</Th>
                <Th>Agency</Th>
                <Th>Opportunity</Th>
                <Th>Due Date</Th>
                <Th>Priority</Th>
                <Th>Requested Service</Th>
                <Th>Status</Th>
                <Th>Assigned To</Th>
                <Th>Received</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((sr) => (
                <tr key={sr.id} className="border-t border-[var(--border-soft)] hover:bg-sky-500/5 transition-colors" data-testid={`eos-inbox-row-${sr.id}`}>
                  <Td>
                    <div className="font-medium text-slate-100">{sr.company_name || "—"}</div>
                    <div className="font-mono text-[10px] text-slate-500 truncate max-w-[180px]">{sr.customer_email}</div>
                  </Td>
                  <Td><div className="flex items-center gap-1.5 text-slate-300"><Building2 className="w-3 h-3 text-slate-500" /><span className="truncate max-w-[140px]">{sr.agency || "—"}</span></div></Td>
                  <Td><div className="truncate max-w-[220px] text-slate-100" title={sr.opportunity_name}>{sr.opportunity_name || "—"}</div>{sr.solicitation_number && <div className="font-mono text-[10px] text-slate-500">{sr.solicitation_number}</div>}</Td>
                  <Td>{dueChip(sr.desired_completion_date)}</Td>
                  <Td>{priorityChip(sr.priority)}</Td>
                  <Td><div className="flex items-center gap-1.5 text-slate-300"><Briefcase className="w-3 h-3 text-slate-500" /><span className="font-mono text-[10px] tracking-widest uppercase">{(sr.requested_service || "").replace(/_/g, " ")}</span></div></Td>
                  <Td>{statusChip(sr.status)}</Td>
                  <Td><span className="text-slate-300 text-xs">{sr.assigned_to || <span className="text-slate-500 italic">Unassigned</span>}</span></Td>
                  <Td><div className="flex items-center gap-1.5 text-slate-400 text-xs"><Calendar className="w-3 h-3 text-slate-500" /><span>{fmtDate(sr.submitted_at)}</span></div></Td>
                  <Td>
                    <button
                      onClick={() => openEngagement(sr)}
                      data-testid={`eos-inbox-open-${sr.id}`}
                      className="inline-flex items-center gap-1 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[10px] tracking-[0.15em] uppercase font-bold px-2.5 py-1.5"
                    >
                      {sr.engagement_id ? "Open" : "Accept"} <ChevronRight className="w-3 h-3" strokeWidth={2.5} />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {busy && items && <div className="mt-3 text-xs text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Updating…</div>}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color = accent === "rose" ? "text-rose-400" : accent === "amber" ? "text-amber-400" : "text-sky-400";
  return (
    <div className="bg-[var(--bg-secondary)] p-4">
      <div className="tick-label mb-2">{label}</div>
      <div className={`metric-number text-3xl leading-none ${color}`}>{value}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="inline-flex items-center gap-1.5">
      <Filter className="w-3 h-3 text-slate-500" />
      <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="bg-[var(--bg-primary)] border border-[var(--border-soft)] text-slate-100 text-xs px-2 py-1"
      >
        {options.map((o) => <option key={o || "any"} value={o}>{o || "Any"}</option>)}
      </select>
    </label>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500 whitespace-nowrap">{children}</th>;
const Td = ({ children }) => <td className="px-3 py-3 align-top">{children}</td>;
